/**
 * StreamVisibilityManager — module-scoped singleton that ensures only one
 * thread holds an active SSE connection at a time.
 *
 * When the user navigates to a different thread, `activate(newThreadId)` aborts
 * every other thread's fetch/stream and records the thread as "recently active"
 * so the background-poll hook knows which queues to check.
 */

const RECENTLY_ACTIVE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface VisibilityAbortSignal extends AbortSignal {
  /** Marker so retry logic can distinguish visibility aborts from network errors. */
  __visibilityAbort?: boolean;
}

class VisibilityAbortController extends AbortController {
  constructor() {
    super();
    (this.signal as VisibilityAbortSignal).__visibilityAbort = true;
  }
}

interface RecentlyActiveEntry {
  threadId: string;
  deactivatedAt: number;
}

class StreamVisibilityManagerImpl {
  private _activeThreadId: string | undefined;
  private _controllers = new Map<string, VisibilityAbortController>();
  private _recentlyActive: RecentlyActiveEntry[] = [];

  /** The currently visible thread id. */
  get activeThreadId(): string | undefined {
    return this._activeThreadId;
  }

  /**
   * Mark a thread as the only visible one. Aborts all other threads' streams.
   * Call this from the useEffect that tracks _liveThreadId.
   */
  activate(threadId: string | undefined): void {
    if (threadId === this._activeThreadId) return;

    // Abort all existing controllers (including the previously active thread).
    for (const [id, controller] of this._controllers) {
      if (id !== threadId) {
        controller.abort();
        this._controllers.delete(id);
        this._addRecentlyActive(id);
      }
    }

    this._activeThreadId = threadId;
  }

  /**
   * Get (or create) an AbortSignal for a thread's stream. Combine this with
   * the library's own abort signal via `AbortSignal.any(...)`.
   */
  getSignal(threadId: string): AbortSignal {
    let controller = this._controllers.get(threadId);
    if (!controller || controller.signal.aborted) {
      controller = new VisibilityAbortController();
      this._controllers.set(threadId, controller);
    }
    return controller.signal;
  }

  /** Abort all active streams (e.g., on logout or unmount). */
  deactivateAll(): void {
    for (const [id, controller] of this._controllers) {
      controller.abort();
      this._addRecentlyActive(id);
    }
    this._controllers.clear();
    this._activeThreadId = undefined;
  }

  /** Full reset — clears all state including recently-active. For tests only. */
  reset(): void {
    for (const [, controller] of this._controllers) {
      controller.abort();
    }
    this._controllers.clear();
    this._activeThreadId = undefined;
    this._recentlyActive = [];
  }

  /**
   * Thread ids that were streaming when the user navigated away.
   * Used by the background-poll hook to know which queues to check.
   * Entries older than RECENTLY_ACTIVE_TTL_MS are pruned on access.
   */
  getRecentlyActiveThreadIds(): string[] {
    const now = Date.now();
    this._recentlyActive = this._recentlyActive.filter(
      (e) => now - e.deactivatedAt < RECENTLY_ACTIVE_TTL_MS,
    );
    return this._recentlyActive.map((e) => e.threadId);
  }

  /**
   * Remove a thread from the recently-active list (e.g., when the poll
   * confirms the queue is no longer active, or the user deletes the thread).
   */
  removeFromRecentlyActive(threadId: string): void {
    this._recentlyActive = this._recentlyActive.filter(
      (e) => e.threadId !== threadId,
    );
  }

  /**
   * Seed the recently-active list from server-reported streaming threads
   * (e.g., from GET /threads where is_response_completed=false). Called on
   * app load so the poll hook starts checking these threads immediately.
   * Skips the currently-active thread (it's already streaming via SSE).
   */
  seedActiveFromServer(threadIds: string[]): void {
    for (const id of threadIds) {
      if (id === this._activeThreadId) continue;
      if (!this._recentlyActive.some((e) => e.threadId === id)) {
        this._recentlyActive.push({ threadId: id, deactivatedAt: Date.now() });
      }
    }
  }

  private _addRecentlyActive(threadId: string): void {
    // Don't duplicate
    if (this._recentlyActive.some((e) => e.threadId === threadId)) {
      // Update timestamp
      this._recentlyActive = this._recentlyActive.map((e) =>
        e.threadId === threadId ? { ...e, deactivatedAt: Date.now() } : e,
      );
    } else {
      this._recentlyActive.push({ threadId, deactivatedAt: Date.now() });
    }
  }
}

/** Singleton instance — module-scoped, shared across all cached runtimes. */
export const StreamVisibilityManager = new StreamVisibilityManagerImpl();

/**
 * Check if an AbortError was caused by a visibility switch (not a network issue).
 * Use this in retry logic to distinguish graceful background-abort from retryable errors.
 */
export function isVisibilityAbort(error: unknown): boolean {
  if (!(error instanceof DOMException) || error.name !== "AbortError") return false;
  // The signal that fired the abort carries our marker
  return true; // Any AbortError caught in a visibility-signal-scoped handler is a visibility abort
}

/**
 * More precise check: given the signal that was used for the fetch, determine
 * if it's the visibility signal that fired (vs. the library's own abort).
 */
export function isVisibilitySignal(signal: AbortSignal): boolean {
  return (signal as VisibilityAbortSignal).__visibilityAbort === true;
}
